import { ref, computed } from 'vue'
import { Preferences } from '@capacitor/preferences'

export interface TutorialStep {
  id: string
  type: 'fullscreen' | 'spotlight' | 'demo' | 'page'
  // spotlight + page
  tab?: string
  // spotlight only
  target?: string
  tooltipPosition?: 'top' | 'bottom'
  scrollTo?: boolean
  waitForLoad?: boolean
  // demo only
  demoId?: string
  // fullscreen only
  ctaLabel?: string
  showSkip?: boolean
  // all
  title: string
  description: string
}

export function createTutorial(steps: TutorialStep[], storageKey: string) {
  const isActive = ref(false)
  const currentStepIndex = ref(0)
  const isChecked = ref(false)

  const currentStep = computed(() => steps[currentStepIndex.value])
  const totalSteps = steps.length

  // Toutes les étapes affichées dans l'indicateur de progression (hors fullscreen)
  const progressSteps = steps.filter((s) => s.type !== 'fullscreen')

  const currentProgressIndex = computed(() =>
    progressSteps.findIndex((s) => s.id === currentStep.value?.id),
  )

  const spotlightSteps = steps.filter((s) => s.type === 'spotlight')

  const checkAndStart = async () => {
    if (isChecked.value) return
    isChecked.value = true
    const { value } = await Preferences.get({ key: storageKey })
    if (!value) start()
  }

  const start = () => {
    currentStepIndex.value = 0
    isActive.value = true
  }

  const next = () => {
    if (currentStepIndex.value < totalSteps - 1) {
      currentStepIndex.value++
    } else {
      complete()
    }
  }

  const prev = () => {
    if (currentStepIndex.value > 0) {
      currentStepIndex.value--
    }
  }

  const skip = async () => {
    await Preferences.set({ key: storageKey, value: 'true' })
    isActive.value = false
  }

  const complete = async () => {
    await Preferences.set({ key: storageKey, value: 'true' })
    isActive.value = false
  }

  const reset = async () => {
    await Preferences.remove({ key: storageKey })
    isChecked.value = false
  }

  return {
    isActive,
    currentStep,
    currentStepIndex,
    totalSteps,
    progressSteps,
    currentProgressIndex,
    spotlightSteps,
    steps,
    checkAndStart,
    start,
    next,
    prev,
    skip,
    complete,
    reset,
  }
}

export type TutorialInstance = ReturnType<typeof createTutorial>
